import { Injectable } from '@angular/core';
import {
  ExecutionSource,
  GetQueryHistoryQuery,
  GetQueryHistoryQueryVariables,
  GetQueryWithExecutionRecordsQuery,
  GetQueryWithExecutionRecordsQueryVariables,
  QueryExecutionRecord,
  QueryStatus,
  QueryWithExecutionRecord,
} from '../../../API';
import { GetQueryHistory, GetQueryWithExecutionRecords } from '../../../graphql/queries';
import { ApiService } from '../../shared/services/api.service';
import { UserManagementService } from '../../shared/services/user-management.service';
import { SegmentHistoryRecord, WorkbenchHistoryRecord } from '../models/history-model';

@Injectable({
  providedIn: 'root',
})
export class QueryHistoryService {
  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
  ) {}

  async getWorkbenchHistory(): Promise<WorkbenchHistoryRecord[]> {
    const records = await this.getHistoryBySource(ExecutionSource.WORKBENCH);

    return records.map((record) => this.mapWorkbenchRow(record, 'Workbench'));
  }

  async getSchedulerHistory(): Promise<WorkbenchHistoryRecord[]> {
    const records = await this.getHistoryBySource(ExecutionSource.SCHEDULER);

    return records.map((record) => this.mapWorkbenchRow(record, 'Scheduler'));
  }

  async getSegmentHistory(): Promise<SegmentHistoryRecord[]> {
    const records = await this.getHistoryBySource(ExecutionSource.SEGMENT);

    return records.map((record) => ({
      executionId: record.executionId,
      queryId: record.queryId?.trim() || record.executionId,
      segmentName: record.name?.trim() || '-',
      segmentDescription: record.description ?? '-',
      segmentedFans: Number(record.segmentedFanCount ?? 0),
      status: record.status ?? QueryStatus.DRAFT,
      createdOn: record.createdAt || record.executedAt || record.lastModifiedAt || '',
      createdBy: record.createdBy?.trim() || record.executedBy?.trim() || record.lastModifiedBy?.trim() || '-',
    }));
  }

  async getQueryRecordById(queryId: string, executionId: string): Promise<QueryWithExecutionRecord | null> {
    if (!queryId || !executionId) {
      return null;
    }
    const orgId = this.userManagementService.getOrgId();

    if (!orgId) {
      throw new Error('Unable to load query details: orgId is missing for the current user.');
    }

    const response = await this.apiService.query<
      GetQueryWithExecutionRecordsQuery,
      GetQueryWithExecutionRecordsQueryVariables
    >(GetQueryWithExecutionRecords, {
      executionId,
      orgId,
      queryId,
    });

    return response.data?.GetQueryWithExecutionRecords ?? null;
  }

  private async getHistoryBySource(executionSource: ExecutionSource): Promise<GetQueryHistoryQuery['GetQueryHistory']> {
    const orgId = this.userManagementService.getOrgId();

    if (!orgId) {
      throw new Error('Unable to load query history: orgId is missing for the current user.');
    }

    const response = await this.apiService.query<GetQueryHistoryQuery, GetQueryHistoryQueryVariables>(GetQueryHistory, {
      input: {
        pkName: 'orgId',
        pkValue: orgId,
        skName: 'executionSource',
        skValue: executionSource,
      },
    });

    return response.data?.GetQueryHistory ?? [];
  }

  private mapWorkbenchRow(
    record: QueryExecutionRecord,
    querySource: WorkbenchHistoryRecord['querySource'],
  ): WorkbenchHistoryRecord {
    return {
      orgId: record.orgId,
      executionId: record.executionId,
      athenaExecutionId: record.athenaExecutionId,
      queryId: record.queryId?.trim() || record.executionId,
      queryName: record.name?.trim() || '-',
      queryType: record.queryType,
      status: record.status ?? QueryStatus.DRAFT,
      querySource,
      schedulerName: record.schedulerName?.trim() || '-',
      lastModifiedAt: record.lastModifiedAt || record.executedAt || record.createdAt || '',
      lastModifiedBy: record.lastModifiedBy?.trim() || record.executedBy?.trim() || record.createdBy?.trim() || '-',
    };
  }

  getStatusColor(status: QueryStatus): string {
    switch (status) {
      case QueryStatus.IN_PROGRESS:
        return 'warning';
      case QueryStatus.EXECUTED:
        return 'success';
      case QueryStatus.FAILED:
      case QueryStatus.VALIDATION_FAILED:
        return 'error';
      case QueryStatus.DRAFT:
        return 'warning';
      case QueryStatus.VALIDATED:
        return 'success';
      default:
        return 'default';
    }
  }

  getDisplayStatus(status: QueryStatus): string {
    switch (status) {
      case QueryStatus.IN_PROGRESS:
        return 'In-progress';
      case QueryStatus.EXECUTED:
        return 'Success';
      case QueryStatus.FAILED:
        return 'Failed';
      case QueryStatus.DRAFT:
        return 'Draft';
      case QueryStatus.VALIDATED:
        return 'Validated';
      case QueryStatus.VALIDATION_FAILED:
        return 'Validation Failed';
      default:
        return status;
    }
  }
}
